import axios from 'axios';

const YOUTUBE_API_KEY = import.meta.env.VITE_YOUTUBE_API_KEY;

// Channel configurations
export const CHANNELS = {
  MUSIC: {
    id: 'UCJ01etj6Rs5obeQU0D0Ntkw',
    name: 'A5omic Music',
    handle: '@A5omic_',
    type: 'music' as const
  },
  PERSONAL: {
    id: 'UCgymwpmoEVa7QLyO4ZJnJ1w',
    name: 'A5omic Personal',
    handle: '@a5omic',
    type: 'personal' as const
  }
};

export interface YouTubeStats {
  subscriberCount: string;
  viewCount: string;
  videoCount: string;
  channelName: string;
  channelUrl: string;
  channelThumbnail: string;
  channelType: 'music' | 'personal';
}

export interface YouTubeVideo {
  id: string;
  title: string;
  description: string;
  thumbnail: string;
  publishedAt: string;
  viewCount: string;
  likeCount: string;
  commentCount: string;
  isShort: boolean;
  url: string;
  duration: string;
}

export const fetchChannelStats = async (channelConfig: typeof CHANNELS.MUSIC | typeof CHANNELS.PERSONAL): Promise<YouTubeStats> => {
  try {
    const response = await axios.get(
      `https://www.googleapis.com/youtube/v3/channels?part=statistics,snippet,brandingSettings&id=${channelConfig.id}&key=${YOUTUBE_API_KEY}`
    );

    const channel = response.data.items[0];
    return {
      subscriberCount: channel.statistics.subscriberCount,
      viewCount: channel.statistics.viewCount,
      videoCount: channel.statistics.videoCount,
      channelName: channel.snippet.title,
      channelUrl: `https://www.youtube.com/channel/${channelConfig.id}`,
      channelThumbnail: channel.snippet.thumbnails.default.url,
      channelType: channelConfig.type,
    };
  } catch (error) {
    console.error('Error fetching YouTube stats:', error);
    throw error;
  }
};

export const fetchVideos = async (channelConfig: typeof CHANNELS.MUSIC | typeof CHANNELS.PERSONAL): Promise<YouTubeVideo[]> => {
  try {
    let allVideoIds: string[] = [];
    let allVideoData: any[] = [];

    // For music channels (often Topic channels), try multiple approaches
    if (channelConfig.type === 'music') {
      // Method 1: Try uploads playlist first for music channels
      try {
        // console.log(`Trying uploads playlist for music channel: ${channelConfig.name}`);
        const uploadsPlaylistId = channelConfig.id.replace('UC', 'UU');
        
        const playlistResponse = await axios.get(
          `https://www.googleapis.com/youtube/v3/playlistItems?part=snippet&playlistId=${uploadsPlaylistId}&maxResults=50&key=${YOUTUBE_API_KEY}`
        );

        if (playlistResponse.data.items && playlistResponse.data.items.length > 0) {
          // console.log(`Found ${playlistResponse.data.items.length} items in uploads playlist`);
          allVideoIds = playlistResponse.data.items.map((item: any) => item.snippet.resourceId.videoId);
          // Transform playlist items to match search API format
          allVideoData = playlistResponse.data.items.map((item: any) => ({
            id: { videoId: item.snippet.resourceId.videoId },
            snippet: item.snippet
          }));
        }
      } catch (playlistError) {
        // console.warn('Could not fetch from uploads playlist:', playlistError);
      }

      // Method 2: Search by channel name + "Topic" for auto-generated music channels
      if (allVideoIds.length === 0) {
        try {
          // console.log(`Searching by channel name for music content`);
          const channelNameSearchResponse = await axios.get(
            `https://www.googleapis.com/youtube/v3/search?part=snippet&q="${channelConfig.name.replace(' Music', '').replace(' ', '%20')}"&type=video&maxResults=50&key=${YOUTUBE_API_KEY}`
          );

          if (channelNameSearchResponse.data.items && channelNameSearchResponse.data.items.length > 0) {
            // console.log(`Found ${channelNameSearchResponse.data.items.length} videos by name search`);
            // Filter for videos that are likely from this artist (check channel title)
            const artistVideos = channelNameSearchResponse.data.items.filter((item: any) => 
              item.snippet.channelTitle.toLowerCase().includes('topic') ||
              item.snippet.channelTitle.toLowerCase().includes(channelConfig.name.toLowerCase().replace(' music', '')) ||
              item.snippet.channelId === channelConfig.id
            );
            
            if (artistVideos.length > 0) {
              allVideoIds = artistVideos.map((item: any) => item.id.videoId);
              allVideoData = artistVideos;
            }
          }
        } catch (nameSearchError) {
          // console.warn('Name-based search failed:', nameSearchError);
        }
      }
    }

    // Method 3: Standard video search (for personal channels or as fallback)
    if (allVideoIds.length === 0) {
      // console.log(`Trying standard channel search for: ${channelConfig.name}`);
      const videosResponse = await axios.get(
        `https://www.googleapis.com/youtube/v3/search?part=snippet&channelId=${channelConfig.id}&maxResults=50&order=date&type=video&key=${YOUTUBE_API_KEY}`
      );

      if (videosResponse.data.items && videosResponse.data.items.length > 0) {
        // console.log(`Found ${videosResponse.data.items.length} videos in standard search`);
        allVideoIds = videosResponse.data.items.map((item: any) => item.id.videoId);
        allVideoData = videosResponse.data.items;
      }
    }

    // Method 4: Try a broader search without channelId filter
    if (allVideoIds.length === 0) {
      try {
        // console.log(`Trying broad search for: ${channelConfig.name}`);
        const broadSearchResponse = await axios.get(
          `https://www.googleapis.com/youtube/v3/search?part=snippet&channelId=${channelConfig.id}&maxResults=50&order=relevance&key=${YOUTUBE_API_KEY}`
        );
        
        if (broadSearchResponse.data.items) {
          const videoItems = broadSearchResponse.data.items.filter((item: any) => item.id.kind === 'youtube#video');
          if (videoItems.length > 0) {
            // console.log(`Found ${videoItems.length} videos in broad search`);
            allVideoIds = videoItems.map((item: any) => item.id.videoId);
            allVideoData = videoItems;
          }
        }
      } catch (broadSearchError) {
        // console.warn('Broad search also failed:', broadSearchError);
      }
    }

    if (allVideoIds.length === 0) {
      // console.warn(`No videos found for channel ${channelConfig.name} (${channelConfig.id})`);
      // console.warn('This might be a Topic channel or the channel might not have any videos accessible via the API');
      return [];
    }

    // Get video statistics and content details
    const videoIds = allVideoIds.join(',');
    const statsResponse = await axios.get(
      `https://www.googleapis.com/youtube/v3/videos?part=statistics,contentDetails,snippet&id=${videoIds}&key=${YOUTUBE_API_KEY}`
    );

    // Helper function to format duration from ISO 8601 to readable format
    const formatDuration = (duration: string) => {
      const match = duration.match(/PT(\d+H)?(\d+M)?(\d+S)?/);
      if (!match) return '00:00';
      
      const hours = (match[1] || '').replace('H', '');
      const minutes = (match[2] || '').replace('M', '');
      const seconds = (match[3] || '').replace('S', '');
      
      let result = '';
      if (hours) result += `${hours}:`;
      if (minutes) result += `${minutes.padStart(2, '0')}:`;
      else result += '00:';
      if (seconds) result += seconds.padStart(2, '0');
      else result += '00';
      
      return result;
    };

    // Combine video data with statistics
    return statsResponse.data.items.map((statsItem: any) => {
      // Find the corresponding original item
      const originalItem = allVideoData.find((item: any) => {
        const videoId = item.id?.videoId || item.snippet?.resourceId?.videoId;
        return videoId === statsItem.id;
      });

      const videoId = statsItem.id;
      const snippet = statsItem.snippet || originalItem?.snippet;
      const stats = statsItem.statistics;
      const contentDetails = statsItem.contentDetails;
      
      if (!snippet) {
        // console.warn(`No snippet data for video ${videoId}`);
        return null;
      }

      const isShort = (snippet.description?.toLowerCase().includes('#shorts') ||
                      snippet.title?.toLowerCase().includes('#shorts')) ?? false;

      return {
        id: videoId,
        title: snippet.title || 'Untitled',
        description: snippet.description || '',
        thumbnail: snippet.thumbnails?.high?.url || snippet.thumbnails?.default?.url || '',
        publishedAt: snippet.publishedAt || '',
        viewCount: stats?.viewCount || '0',
        likeCount: stats?.likeCount || '0',
        commentCount: stats?.commentCount || '0',
        isShort,
        url: `https://www.youtube.com/watch?v=${videoId}`,
        duration: contentDetails ? formatDuration(contentDetails.duration) : '00:00'
      };
    }).filter(Boolean); // Remove any null entries
  } catch (error) {
    console.error('Error fetching YouTube videos:', error);
    throw error;
  }
};

// Helper functions for specific channels
export const fetchMusicChannelData = async () => {
  const [stats, videos] = await Promise.all([
    fetchChannelStats(CHANNELS.MUSIC),
    fetchVideos(CHANNELS.MUSIC)
  ]);
  return { stats, videos };
};

export const fetchPersonalChannelData = async () => {
  const [stats, videos] = await Promise.all([
    fetchChannelStats(CHANNELS.PERSONAL),
    fetchVideos(CHANNELS.PERSONAL)
  ]);
  return { stats, videos };
};

export const fetchAllChannelsData = async () => {
  const [musicData, personalData] = await Promise.all([
    fetchMusicChannelData(),
    fetchPersonalChannelData()
  ]);
  return { music: musicData, personal: personalData };
}; 